// チュートリアル（サンドボックス）専用の型定義。
// 実画面の型（types.ts）とは独立させ、DB 保存を一切行わない練習用データに限定する。

// ステータスはルームごとに追加できるため文字列として扱う（基本の4種は下記）。
export type SandboxStatus = string;
export type SandboxPriority = "高" | "中" | "低" | "なし";

/** ステータスの定義（名前＋色）。ルーム設定で追加されうる */
export interface SandboxStatusDef {
  name: string;
  color: string;
}

/** 標準ステータス（未対応 / 対応中 / 完了 / 非表示） */
export const SANDBOX_DEFAULT_STATUSES: SandboxStatusDef[] = [
  { name: "未対応", color: "#8A94A6" },
  { name: "対応中", color: "#3989FA" },
  { name: "完了", color: "#40C063" },
  { name: "非表示", color: "#8A94A6" },
];

/** 全体設定ダイアログのタブ */
export type SettingsTab = "status" | "tags" | "custom" | "autoReminder";

/** 担当者・依頼者の選択肢（練習用ダミーメンバー） */
export const SANDBOX_MEMBERS: string[] = [
  "あなた",
  "佐藤 太郎",
  "鈴木 花子",
  "高橋 健",
];

export interface SandboxTag {
  id: number;
  name: string;
  color: string;
}

/** タグの選択肢（練習用ダミータグ） */
export const SANDBOX_TAGS: SandboxTag[] = [
  { id: 1, name: "重要", color: "#F85B52" },
  { id: 2, name: "会議", color: "#3989FA" },
  { id: 3, name: "資料作成", color: "#40C063" },
];

export interface SandboxCustomColumn {
  id: number;
  name: string;
  type: "text" | "select";
  options?: string[];
}

/** カスタム項目の定義（練習用ダミー） */
export const SANDBOX_CUSTOM_COLUMNS: SandboxCustomColumn[] = [
  { id: 1, name: "見積工数", type: "text" },
  { id: 2, name: "カテゴリ", type: "select", options: ["開発", "営業", "総務"] },
];

/** タスクに設定されたリマインダー（練習用） */
export interface SandboxReminder {
  id: string;
  /** 通知日時の表示文字列（例：2026/07/10 08:00） */
  label: string;
  /** 自動リマインダー設定によって作成されたものなら true */
  isAuto: boolean;
}

/**
 * 自動リマインダー設定のフォーム状態（プロジェクトごとの 1 ルール）。
 * 実画面 GeneralSettingsDialog の「自動リマインダー」タブを簡略再現する。
 * 実画面では 1 プロジェクトに複数のルールを設定できるが、
 * チュートリアルでは 1 ルールを作る流れで代表的な操作を体験する。
 */
export interface SandboxAutoReminderForm {
  isEnabled: boolean;
  /** ルール名（任意） */
  ruleName: string;
  /** 期限の何日前に通知するか */
  daysBefore: number;
  /** 通知する時刻（0-23 時） */
  hour: number;
  /** 対象にする優先度 */
  priorities: SandboxPriority[];
  /** 対象にするステータス（完了・非表示は常に除外） */
  statuses: string[];
  /** 対象にするタグ ID（指定したタグをすべて持つタスクが対象。空なら条件なし） */
  tags: number[];
  /** 保存時に既存タスクにも適用するか */
  applyToExisting: boolean;
}

export interface SandboxTask {
  id: string;
  title: string;
  status: SandboxStatus;
  completed: boolean;
  priority: SandboxPriority;
  memo: string;
  /** 開始日（YYYY-MM-DD）。未設定なら undefined */
  startDate?: string;
  /** 終了日（YYYY-MM-DD）。期間設定時のみ */
  endDate?: string;
  /** 担当者（表示名の配列） */
  assignees: string[];
  /** 依頼者（表示名の配列） */
  requesters: string[];
  /** 選択中のタグ名 */
  tags: string[];
  /** カスタム項目の値（customColumnId -> 値） */
  customValues: Record<number, string>;
  /** 完了日時（ISO 文字列）。完了時のみ設定される */
  completedAt?: string;
  /** 完了者の表示名。完了時のみ設定される */
  completedBy?: string;
  /** 繰り返しルールから自動作成されたタスクなら true（繰り返しチュートリアル用） */
  sourceRecurring?: boolean;
  /** 設定済みのリマインダー（リマインダーチュートリアル用） */
  reminders?: SandboxReminder[];
}

export interface SandboxList {
  id: string;
  name: string;
  tasks: SandboxTask[];
}

/** タスク作成ダイアログの編集中フォーム状態 */
export interface SandboxTaskDraft {
  open: boolean;
  title: string;
  priority: SandboxPriority;
  startDate?: string;
  endDate?: string;
  assignees: string[];
  requesters: string[];
  tags: string[];
  customValues: Record<number, string>;
  memo: string;
}

/** ステップを次へ進める「ユーザー操作」の識別子 */
export type TutorialAction =
  | "open-list-dialog"
  | "create-project"
  | "open-task-dialog"
  | "create-task"
  | "select-task"
  | "change-priority"
  | "open-status-menu"
  | "complete-task"
  | "toggle-show-completed"
  // ルームごとの設定チュートリアル用
  | "open-settings"
  | "close-settings"
  | "open-tags-tab"
  | "open-custom-tab"
  | "add-status"
  | "add-tag"
  | "add-custom-column"
  // カスタム項目チュートリアル用
  | "add-custom-text"
  | "add-custom-select"
  | "open-custom-display"
  | "save-custom-display"
  | "detail-open-status"
  // 繰り返し作成チュートリアル用
  | "recurrence-open-dialog"
  | "recurrence-show-form"
  | "recurrence-save"
  | "recurrence-open-list"
  | "recurrence-edit-rule"
  | "recurrence-close-list"
  | "recurrence-edit-source"
  | "select-auto-task"
  | "recurrence-edit-auto"
  // リマインダーチュートリアル用
  | "reminder-open-dialog"
  | "reminder-add"
  | "reminder-close"
  | "open-auto-reminder-tab"
  | "toggle-auto-reminder"
  | "save-auto-reminder"
  // ダッシュボードのカスタマイズチュートリアル用（PC 版限定）
  | "dash-scorecard-edit"
  | "dash-scorecard-add-deadline-card"
  | "dash-scorecard-edit-deadline-card"
  | "dash-scorecard-confirm-deadline-card"
  | "dash-scorecard-open-add-menu"
  | "dash-scorecard-add-category"
  | "dash-scorecard-save"
  | "dash-scorecard-personal"
  | "dash-open-column-settings"
  | "dash-column-personal"
  | "dash-column-save";

export type TutorialPlacement = "top" | "bottom" | "left" | "right" | "center";

export interface TutorialStep {
  id: string;
  /** ハイライト対象要素の data-tut 値。null の場合はハイライトなし（説明のみ） */
  targetKey: string | null;
  title: string;
  /** \n で段落分割してレンダリングする */
  body: string;
  /**
   * @deprecated 現在は未使用。説明カードは右下固定表示（ハイライトとかぶる場合のみ
   * 自動退避）となったため、ステップごとの配置指定は行わない。
   */
  placement?: TutorialPlacement;
  /**
   * 設定された場合、このアクションが発生したときに次のステップへ進む（操作ステップ）。
   * 未設定の場合は「次へ」ボタンで進む説明ステップ。
   */
  expectAction?: TutorialAction;
}
